import { API_ROUTES } from '@/constants/apiRoutes';
import { ApiResponse, PaginatedApiResponse } from '@/react-query/context/ApiClientContextController';
import { queryFactoryOptions } from '@/react-query/utills/queryFactoryOptions';
import { Charges, ChargeSubcategory, ChargeSubcategoryDetail, CurrentCharges } from '@/services/charges/charges.types';
import { AxiosInstance } from 'axios';

export interface ChargeParams {
  agentType?: string;
  pageNo?: number;
  fetch?: number;
  search?: string;
}
export interface ChargeCategoryParams {
  affiliateId?: string;
  row?: number;
  pageNo?: number;
  fromDate?: number;
  toDate?: number;
  fetch?: number;
  createdDate?: number;
  search?: string;
}
export interface CurrentChargeParams {
  affiliateId?: string;
}

export interface ChargeDetailParams {
  chargeId?: string;
  agentType?: string;
  categoryName?: string;
  description?: string;
}

export interface ChargeSubcategoryParams {
  row?: number;
  search?: string;
  fromDateMs?: number;
  toDateMs?: number;
  fetch?: number;
  chargeCategoryId?: string;
}

export type ChargeType = 'club' | 'affiliate' | 'customer';

export const getCharges = (client: AxiosInstance) => async (params?: ChargeParams) => {
  return (await client.get<PaginatedApiResponse<Charges[]>>(API_ROUTES.CHARGE_MANAGEMENT_LIST, { params })).data;
};

export const chargesQueries = {
  all: (type: ChargeType) => [type, 'charges'],
  list: (type: ChargeType, params?: ChargeParams) =>
    queryFactoryOptions<PaginatedApiResponse<Charges[]>>({
      queryKey: [type, 'charges', 'list', params],
      queryFn: (client: AxiosInstance) => () => getCharges(client)(params),
    }),
};

export const getCurrentCharges = (client: AxiosInstance) => async (params?: CurrentChargeParams) => {
  return (await client.get<PaginatedApiResponse<CurrentCharges>>(API_ROUTES.GET_CURRENT_CHARGE, { params })).data;
};

export const CurrentChargesQueries = {
  all: () => ['currentcharges'],
  list: (params?: CurrentChargeParams) =>
    queryFactoryOptions<ApiResponse<CurrentCharges>>({
      queryKey: ['currentcharges', 'list', params],
      queryFn: (client: AxiosInstance) => () => getCurrentCharges(client)(params),
    }),
};

export const getChargesDetail = (client: AxiosInstance) => async (params?: ChargeDetailParams) => {
  return (await client.get<PaginatedApiResponse<Charges>>(API_ROUTES.GET_CHARGE_DETAIL, { params })).data;
};

export const ChargesDetailQueries = {
  all: () => ['chargesDetail'],
  list: (params?: ChargeDetailParams) =>
    queryFactoryOptions<ApiResponse<ChargeDetailParams>>({
      queryKey: ['chargesDetail', 'list', params],
      queryFn: (client: AxiosInstance) => () => getChargesDetail(client)(params),
    }),
};

export const getChargeSubcategory = (client: AxiosInstance) => async (params?: ChargeSubcategoryParams) => {
  return (
    await client.get<PaginatedApiResponse<ChargeSubcategory[]>>(API_ROUTES.GET_CHARGE_SUBCATEGORY, {
      params,
    })
  ).data;
};

export const getChargesSubcategoryDetail = (client: AxiosInstance) => async (chargeSubCategoryId?: string) => {
  return (
    await client.get<ApiResponse<ChargeSubcategoryDetail>>(API_ROUTES.GET_CHARGE_SUBCATEGORY_DETAIL, {
      params: { chargeSubCategoryId },
    })
  ).data;
};

export const chargeSubcategoryQueries = {
  all: () => ['chargesSubcategory'],
  list: (params?: ChargeSubcategoryParams) =>
    queryFactoryOptions<PaginatedApiResponse<ChargeSubcategory[]>>({
      queryKey: [...chargeSubcategoryQueries.all(), 'list', params],
      queryFn: (client: AxiosInstance) => () => getChargeSubcategory(client)(params),
    }),
  detail: (chargeSubCategoryId: string) =>
    queryFactoryOptions<ApiResponse<ChargeSubcategoryDetail>>({
      queryKey: [...chargeSubcategoryQueries.all(), 'detail', chargeSubCategoryId],
      queryFn: (client: AxiosInstance) => () => getChargesSubcategoryDetail(client)(chargeSubCategoryId),
    }),
};
